/**
 * Mock dataset for the Western Express prototype.
 *
 * One `Driver` record per truck carries the full active-context payload used by
 * both the task feed drill-down and the Freight Status board. `tasks` reference
 * drivers by id and model the four task states from the design review.
 */

import { hash } from "./hash";
import type { DispatchTask, Driver, DriverActivityEvent, DriverStop, RouteModel } from "./types";

function route(
  origin: string,
  destination: string,
  cities: RouteModel["cities"],
  segments: RouteModel["segments"],
): RouteModel {
  return { origin, destination, cities, segments };
}

const BASE_DRIVERS: Driver[] = [
  {
    id: "drv_marcus_bell",
    name: "Marcus Bell",
    truck: "4471",
    division: "100",
    divisionLabel: "OTR",
    status: "AC",
    statusLabel: "Available",
    customer: "Kroger DC",
    customerCity: "Memphis, TN",
    location: "I-40 near Jackson, TN",
    velocityMph: 8,
    velocityTrend: "down",
    fuelLevel: 42,
    loadType: "Live unload",
    destination: "Memphis, TN",
    appointment: { kind: "window", start: "12:00", end: "14:00" },
    nextStop: "Receiver appt — Kroger DC",
    appointmentRisk: {
      atRisk: true,
      projected: "14:10",
      target: "Window ends 14:00",
      delta: "+10m",
      reason: "Traffic",
    },
    pta: {
      time: "16:30",
      risk: { atRisk: true, projected: "16:30", target: "Planned 15:00", delta: "+1h 30m", reason: "HOS + Traffic" },
    },
    hos: {
      primaryLabel: "Drive",
      primaryValue: "4h 12m left",
      primaryContext: "vs 3h 45m to appt",
      clocks: [
        { label: "Drive", value: "4h 12m" },
        { label: "Shift", value: "6h 30m" },
        { label: "Break", value: "8h 00m" },
        { label: "Cycle", value: "34h 10m" },
      ],
    },
    tags: ["pta", "traffic"],
    route: route(
      "Jackson, TN",
      "Memphis, TN",
      [
        { label: "Jackson, TN", at: 0 },
        { label: "Humboldt", at: 0.34 },
        { label: "Brownsville", at: 0.58 },
        { label: "Memphis, TN", at: 1 },
      ],
      [
        { level: "clear", weight: 3 },
        { level: "moderate", weight: 1 },
        { level: "heavy", weight: 1.4 },
        { level: "moderate", weight: 1 },
        { level: "clear", weight: 2.4 },
      ],
    ),
  },
  {
    id: "drv_tanya_martinez",
    name: "Tanya Martinez",
    truck: "2218",
    division: "100",
    divisionLabel: "OTR",
    status: "AC",
    statusLabel: "Available",
    location: "Louisville, KY",
    velocityMph: 12,
    velocityTrend: "down",
    fuelLevel: 78,
    loadType: "Empty",
    destination: "—",
    hos: {
      primaryLabel: "Drive",
      primaryValue: "2h 15m left",
      clocks: [
        { label: "Drive", value: "2h 15m" },
        { label: "Shift", value: "4h 05m" },
        { label: "Break", value: "8h 00m" },
        { label: "Cycle", value: "28h 40m" },
      ],
    },
    tags: ["empty_call"],
    route: route(
      "Louisville, KY",
      "Charlotte, NC",
      [
        { label: "Louisville, KY", at: 0 },
        { label: "Lexington", at: 0.3 },
        { label: "Charlotte, NC", at: 1 },
      ],
      [
        { level: "clear", weight: 4 },
        { level: "moderate", weight: 1 },
        { level: "clear", weight: 3 },
      ],
    ),
  },
  {
    id: "drv_deshawn_riley",
    name: "DeShawn Riley",
    truck: "6635",
    division: "150",
    divisionLabel: "Regional",
    status: "BD",
    statusLabel: "Broken down",
    customer: "Pilot Travel Center",
    customerCity: "Hayti, MO",
    location: "I-55 MM 118",
    velocityMph: 0,
    velocityTrend: "flat",
    fuelLevel: 31,
    loadType: "Drop",
    destination: "St. Louis, MO",
    nextStop: "Awaiting recovery",
    hos: {
      primaryLabel: "On-duty",
      primaryValue: "0h 00m left",
      primaryContext: "stopped",
      clocks: [
        { label: "Drive", value: "5h 40m" },
        { label: "Shift", value: "0h 00m" },
        { label: "Break", value: "8h 00m" },
        { label: "Cycle", value: "41h 20m" },
      ],
    },
    tags: ["ddi"],
    route: route(
      "Hayti, MO",
      "St. Louis, MO",
      [
        { label: "Hayti, MO", at: 0 },
        { label: "Cape Girardeau", at: 0.45 },
        { label: "St. Louis, MO", at: 1 },
      ],
      [
        { level: "clear", weight: 5 },
        { level: "clear", weight: 5 },
      ],
    ),
  },
  {
    id: "drv_priya_nair",
    name: "Priya Nair",
    truck: "5012",
    division: "125",
    divisionLabel: "Dedicated",
    status: "T",
    statusLabel: "Team",
    customer: "Walmart DC 7037",
    customerCity: "Sterling, IL",
    location: "I-70 near Effingham, IL",
    velocityMph: 60,
    velocityTrend: "flat",
    fuelLevel: 64,
    loadType: "Live unload",
    destination: "Sterling, IL",
    appointment: { kind: "window", start: "09:00", end: "11:00" },
    nextStop: "Receiver appt — Walmart DC 7037",
    appointmentRisk: { atRisk: false, projected: "10:20", target: "Window ends 11:00" },
    pta: { time: "10:20", risk: { atRisk: false, projected: "10:20", target: "Planned 11:30" } },
    hos: {
      primaryLabel: "Drive",
      primaryValue: "1h 05m left",
      primaryContext: "reset planned en route",
      clocks: [
        { label: "Drive", value: "1h 05m" },
        { label: "Shift", value: "2h 10m" },
        { label: "Break", value: "8h 00m" },
        { label: "Cycle", value: "19h 30m" },
      ],
    },
    tags: ["split_sleeper"],
    route: route(
      "Effingham, IL",
      "Sterling, IL",
      [
        { label: "Effingham, IL", at: 0 },
        { label: "Bloomington", at: 0.5 },
        { label: "Sterling, IL", at: 1 },
      ],
      [
        { level: "clear", weight: 6 },
        { level: "moderate", weight: 1 },
        { level: "clear", weight: 3 },
      ],
    ),
  },
  {
    id: "drv_brian_thompson",
    name: "Brian Thompson",
    truck: "3099",
    division: "100",
    divisionLabel: "OTR",
    status: "AC",
    statusLabel: "Available",
    customer: "Amazon DC MEM1",
    customerCity: "Memphis, TN",
    location: "I-22 near Holly Springs, MS",
    velocityMph: 58,
    velocityTrend: "flat",
    fuelLevel: 55,
    loadType: "Live unload",
    destination: "Memphis, TN",
    appointment: { kind: "window", start: "08:00", end: "10:00" },
    nextStop: "Receiver appt — Amazon DC MEM1",
    appointmentRisk: { atRisk: false, projected: "08:45", target: "Window ends 10:00" },
    pta: { time: "08:45", risk: { atRisk: false, projected: "08:45", target: "Planned 09:30" } },
    hos: {
      primaryLabel: "Drive",
      primaryValue: "3h 20m left",
      clocks: [
        { label: "Drive", value: "3h 20m" },
        { label: "Shift", value: "5h 50m" },
        { label: "Break", value: "8h 00m" },
        { label: "Cycle", value: "30h 15m" },
      ],
    },
    tags: [],
    route: route(
      "Holly Springs, MS",
      "Memphis, TN",
      [
        { label: "Holly Springs, MS", at: 0 },
        { label: "Byhalia", at: 0.5 },
        { label: "Memphis, TN", at: 1 },
      ],
      [
        { level: "clear", weight: 6 },
        { level: "clear", weight: 4 },
      ],
    ),
  },
  {
    id: "drv_olivia_grant",
    name: "Olivia Grant",
    truck: "8842",
    division: "125",
    divisionLabel: "Dedicated",
    status: "AC",
    statusLabel: "Available",
    customer: "Target DC 1202",
    customerCity: "Indianapolis, IN",
    location: "I-65 near Columbus, IN",
    velocityMph: 49,
    velocityTrend: "flat",
    fuelLevel: 48,
    loadType: "Drop",
    destination: "Indianapolis, IN",
    appointment: { kind: "window", start: "11:00", end: "13:00" },
    nextStop: "Receiver appt — Target DC 1202",
    appointmentRisk: { atRisk: false, projected: "11:40", target: "Window ends 13:00" },
    pta: { time: "11:40", risk: { atRisk: false, projected: "11:40", target: "Planned 12:30" } },
    hos: {
      primaryLabel: "Shift",
      primaryValue: "2h 45m left",
      primaryContext: "approaching off-duty",
      clocks: [
        { label: "Drive", value: "2h 45m" },
        { label: "Shift", value: "2h 45m" },
        { label: "Break", value: "0h 30m" },
        { label: "Cycle", value: "22h 05m" },
      ],
    },
    tags: ["split_sleeper"],
    route: route(
      "Columbus, IN",
      "Indianapolis, IN",
      [
        { label: "Columbus, IN", at: 0 },
        { label: "Greenwood", at: 0.6 },
        { label: "Indianapolis, IN", at: 1 },
      ],
      [
        { level: "clear", weight: 6 },
        { level: "moderate", weight: 2 },
        { level: "clear", weight: 2 },
      ],
    ),
  },
  {
    id: "drv_carlos_mendez",
    name: "Carlos Mendez",
    truck: "7120",
    division: "150",
    divisionLabel: "Regional",
    status: "AC",
    statusLabel: "Available",
    customer: "Loves Travel Stop",
    customerCity: "Oklahoma City, OK",
    location: "I-40 near Shawnee, OK",
    velocityMph: 25,
    velocityTrend: "down",
    fuelLevel: 22,
    loadType: "Drop",
    destination: "Oklahoma City, OK",
    nextStop: "Drop — Loves Travel Stop",
    appointmentRisk: { atRisk: true, projected: "17:15", target: "Planned 16:00", delta: "+1h 15m", reason: "Traffic" },
    pta: {
      time: "17:15",
      risk: { atRisk: true, projected: "17:15", target: "Planned 16:00", delta: "+1h 15m", reason: "Traffic" },
    },
    hos: {
      primaryLabel: "Drive",
      primaryValue: "5h 10m left",
      clocks: [
        { label: "Drive", value: "5h 10m" },
        { label: "Shift", value: "7h 20m" },
        { label: "Break", value: "8h 00m" },
        { label: "Cycle", value: "38h 00m" },
      ],
    },
    tags: ["pta", "traffic"],
    route: route(
      "Shawnee, OK",
      "Oklahoma City, OK",
      [
        { label: "Shawnee, OK", at: 0 },
        { label: "Midwest City", at: 0.6 },
        { label: "Oklahoma City, OK", at: 1 },
      ],
      [
        { level: "clear", weight: 4 },
        { level: "heavy", weight: 2 },
        { level: "moderate", weight: 2 },
      ],
    ),
  },
  {
    id: "drv_janet_wu",
    name: "Janet Wu",
    truck: "4456",
    division: "100",
    divisionLabel: "OTR",
    status: "C",
    statusLabel: "Check pre-plan",
    customer: "Home Depot DC 4122",
    customerCity: "Atlanta, GA",
    location: "I-75 near Macon, GA",
    velocityMph: 62,
    velocityTrend: "flat",
    fuelLevel: 71,
    loadType: "Live unload",
    destination: "Atlanta, GA",
    appointment: { kind: "window", start: "13:00", end: "15:00" },
    nextStop: "Receiver appt — Home Depot DC 4122",
    appointmentRisk: { atRisk: false, projected: "13:50", target: "Window ends 15:00" },
    pta: { time: "13:50", risk: { atRisk: false, projected: "13:50", target: "Planned 14:30" } },
    hos: {
      primaryLabel: "Drive",
      primaryValue: "1h 50m left",
      clocks: [
        { label: "Drive", value: "1h 50m" },
        { label: "Shift", value: "3h 30m" },
        { label: "Break", value: "8h 00m" },
        { label: "Cycle", value: "24h 10m" },
      ],
    },
    tags: [],
    route: route(
      "Macon, GA",
      "Atlanta, GA",
      [
        { label: "Macon, GA", at: 0 },
        { label: "Forsyth", at: 0.5 },
        { label: "Atlanta, GA", at: 1 },
      ],
      [
        { level: "clear", weight: 5 },
        { level: "moderate", weight: 3 },
        { level: "heavy", weight: 2 },
      ],
    ),
  },
  {
    id: "drv_derek_olsen",
    name: "Derek Olsen",
    truck: "9033",
    division: "175",
    divisionLabel: "West Coast",
    status: "AC",
    statusLabel: "Available",
    customer: "FedEx Ground",
    customerCity: "Phoenix, AZ",
    location: "I-10 near Casa Grande, AZ",
    velocityMph: 5,
    velocityTrend: "down",
    fuelLevel: 18,
    loadType: "Drop",
    destination: "Phoenix, AZ",
    appointment: { kind: "strict", time: "15:30" },
    nextStop: "Drop — FedEx Ground",
    appointmentRisk: {
      atRisk: true,
      projected: "15:55",
      target: "Appointment time 15:30",
      delta: "+25m",
      reason: "Traffic",
    },
    pta: {
      time: "16:05",
      risk: { atRisk: true, projected: "16:05", target: "Planned 14:45", delta: "+1h 20m", reason: "HOS + Traffic" },
    },
    hos: {
      primaryLabel: "On-duty",
      primaryValue: "6h 30m left",
      clocks: [
        { label: "Drive", value: "6h 30m" },
        { label: "Shift", value: "8h 10m" },
        { label: "Break", value: "8h 00m" },
        { label: "Cycle", value: "44h 50m" },
      ],
    },
    tags: ["pta", "ddi"],
    route: route(
      "Casa Grande, AZ",
      "Phoenix, AZ",
      [
        { label: "Casa Grande, AZ", at: 0 },
        { label: "Chandler", at: 0.6 },
        { label: "Phoenix, AZ", at: 1 },
      ],
      [
        { level: "clear", weight: 4 },
        { level: "heavy", weight: 3 },
        { level: "moderate", weight: 2 },
      ],
    ),
  },
  {
    id: "drv_maria_flores",
    name: "Maria Flores",
    truck: "6201",
    division: "150",
    divisionLabel: "Regional",
    status: "AC",
    statusLabel: "Available",
    customer: "Kroger DC",
    customerCity: "Nashville, TN",
    location: "I-24 near Murfreesboro, TN",
    velocityMph: 34,
    velocityTrend: "down",
    fuelLevel: 60,
    loadType: "Live unload",
    destination: "Nashville, TN",
    appointment: { kind: "window", start: "14:00", end: "16:00" },
    nextStop: "Receiver appt — Kroger DC",
    appointmentRisk: {
      atRisk: true,
      projected: "16:50",
      target: "Window ends 16:00",
      delta: "+50m",
      reason: "Traffic",
    },
    pta: {
      time: "15:20",
      risk: { atRisk: true, projected: "15:20", target: "Planned 14:00", delta: "+1h 20m", reason: "HOS" },
    },
    hos: {
      primaryLabel: "Drive",
      primaryValue: "3h 05m left",
      primaryContext: "home time in 1 day",
      clocks: [
        { label: "Drive", value: "3h 05m" },
        { label: "Shift", value: "5h 15m" },
        { label: "Break", value: "8h 00m" },
        { label: "Cycle", value: "26h 40m" },
      ],
    },
    tags: ["pta", "home_time"],
    route: route(
      "Murfreesboro, TN",
      "Nashville, TN",
      [
        { label: "Murfreesboro, TN", at: 0 },
        { label: "La Vergne", at: 0.5 },
        { label: "Nashville, TN", at: 1 },
      ],
      [
        { level: "clear", weight: 4 },
        { level: "moderate", weight: 2 },
        { level: "heavy", weight: 2 },
        { level: "moderate", weight: 2 },
      ],
    ),
  },
  {
    id: "drv_bianca_ortiz",
    name: "Bianca Ortiz",
    truck: "8931",
    division: "100",
    divisionLabel: "OTR",
    status: "T",
    statusLabel: "At customer",
    customer: "Home Depot RDC",
    customerCity: "Dallas, TX",
    location: "Home Depot RDC — Dallas, TX",
    velocityMph: 0,
    velocityTrend: "flat",
    fuelLevel: 64,
    loadType: "Empty",
    destination: "Dallas, TX",
    hos: {
      primaryLabel: "Drive",
      primaryValue: "8h 05m left",
      clocks: [
        { label: "Drive", value: "8h 05m" },
        { label: "Shift", value: "10h 20m" },
        { label: "Break", value: "8h 00m" },
        { label: "Cycle", value: "48h 50m" },
      ],
    },
    tags: ["empty_call"],
    route: route(
      "Fort Worth, TX",
      "Dallas, TX",
      [
        { label: "Fort Worth, TX", at: 0 },
        { label: "Arlington", at: 0.5 },
        { label: "Dallas, TX", at: 1 },
      ],
      [
        { level: "clear", weight: 4 },
        { level: "moderate", weight: 1 },
        { level: "clear", weight: 3 },
      ],
    ),
  },
];

function apptLabel(driver: Driver): string | undefined {
  const appt = driver.appointment;
  if (!appt) return undefined;
  return appt.kind === "window" ? `${appt.start}–${appt.end}` : appt.time;
}

/** Trim a "City, ST" route label down to the place name only. */
function placeName(label: string): string {
  return label.split(",")[0]?.trim() ?? label;
}

/**
 * Derive a plausible stops timeline for a detailed driver from their existing
 * route, customer, and appointment data. Always yields completed → current →
 * upcoming stops in chronological order, with the receiver as the current focus.
 */
function deriveStops(driver: Driver): DriverStop[] {
  const origin = driver.route.origin;
  const dest = driver.route.destination;
  const receiver = driver.customer ?? "Receiver";
  const receiverCity = driver.customerCity ?? dest;
  const brokenDown = driver.status === "BD";

  const stops: DriverStop[] = [
    {
      id: `${driver.id}_stop_pickup`,
      state: "completed",
      location: `Shipper — ${placeName(origin)} DC`,
      city: origin,
      purpose: "Loaded & departed",
      eta: "06:20",
    },
    {
      id: `${driver.id}_stop_fuel`,
      state: "completed",
      location: hash(driver.id) % 2 === 0 ? "Pilot Travel Center" : "Love's Travel Stop",
      city: driver.route.cities[1]?.label ?? origin,
      purpose: "Fuel & break",
      eta: "09:35",
    },
    {
      id: `${driver.id}_stop_current`,
      state: "current",
      location: brokenDown ? `Roadside — ${driver.location}` : receiver,
      city: receiverCity,
      purpose: brokenDown ? "Awaiting recovery" : (driver.loadType ?? "Delivery"),
      appointment: apptLabel(driver),
      eta: brokenDown ? "ETA unknown" : (driver.appointmentRisk?.projected ?? driver.pta?.time),
    },
    {
      id: `${driver.id}_stop_next`,
      state: "upcoming",
      location: "Next assignment",
      city: dest,
      purpose: "Awaiting pre-plan",
    },
  ];

  // A subset of drivers carry an extra upcoming reload so the timeline length varies.
  if (hash(driver.id) % 3 === 0) {
    stops.push({
      id: `${driver.id}_stop_reload`,
      state: "upcoming",
      location: `Reload — ${placeName(dest)} cross-dock`,
      city: dest,
      purpose: "Drop & hook",
    });
  }

  // Estimated remaining miles to reach each not-yet-completed stop, accumulating
  // a per-leg distance so the figures grow down the timeline.
  let milesAhead = 12 + (hash(`${driver.id}_milebase`) % 28);
  for (const stop of stops) {
    if (stop.state === "completed") continue;
    if (stop.state !== "current") milesAhead += 70 + (hash(`${stop.id}_leg`) % 180);
    stop.milesToStop = milesAhead;
  }

  return stops;
}

/**
 * Derive a chronological activity timeline (empty/loaded calls, arrivals,
 * departures) for a detailed driver from their route and customer.
 */
function deriveActivity(driver: Driver): DriverActivityEvent[] {
  const origin = driver.route.origin;
  const receiver = `${driver.customer ?? "Receiver"} — ${driver.customerCity ?? driver.route.destination}`;
  const shipper = `${placeName(origin)} DC — ${origin}`;

  const events: DriverActivityEvent[] = [
    {
      id: `${driver.id}_act_empty`,
      kind: "empty_call",
      timestamp: "05:10",
      location: shipper,
      detail: "Called empty, trailer dropped",
    },
    {
      id: `${driver.id}_act_loaded`,
      kind: "loaded_call",
      timestamp: "06:05",
      location: shipper,
      detail: `Loaded call · trailer ${5000 + (hash(driver.id) % 4000)}`,
    },
    {
      id: `${driver.id}_act_depart_shipper`,
      kind: "departure",
      timestamp: "06:20",
      location: shipper,
    },
    {
      id: `${driver.id}_act_arrive_fuel`,
      kind: "arrival",
      timestamp: "09:30",
      location: `${driver.route.cities[1]?.label ?? origin} — fuel`,
    },
    {
      id: `${driver.id}_act_depart_fuel`,
      kind: "departure",
      timestamp: "10:05",
      location: `${driver.route.cities[1]?.label ?? origin} — fuel`,
    },
  ];

  // Drivers already at the customer have an arrival on the board; in-transit
  // drivers have their arrival still upcoming, so we only log it for stopped trucks.
  if (driver.velocityMph === 0 || driver.status === "BD") {
    events.push({
      id: `${driver.id}_act_arrive_receiver`,
      kind: "arrival",
      timestamp: "11:48",
      location: receiver,
      detail: driver.status === "BD" ? "Stopped — breakdown" : "Arrived at receiver",
    });
  }

  return events;
}

/** Attach a derived stops + activity timeline to each detailed driver. */
function withItinerary(driver: Driver): Driver {
  return { ...driver, stops: deriveStops(driver), activity: deriveActivity(driver) };
}

export const DRIVERS: Driver[] = BASE_DRIVERS.map(withItinerary);

export const TASKS: DispatchTask[] = [
  {
    id: "task_marcus",
    driverId: "drv_marcus_bell",
    type: "pta_risk",
    state: "suggested",
    urgency: "urgent",
    summary: "Behind schedule — appointment & PTA at risk for live unload in Memphis, TN.",
    recommendation: "Push PTA to 14:30 & notify receiver.",
    cannedMessage: "Hi Marcus, are you still on track for your 12:30 appointment?",
  },
  {
    id: "task_tanya",
    driverId: "drv_tanya_martinez",
    type: "empty_call",
    state: "suggested",
    urgency: "high",
    summary: "Empty call at Louisville, KY — review options to reduce deadhead.",
    recommendation: "Offer reload from Louisville to Charlotte.",
    cannedMessage: "Hi Tanya, want me to look for a reload out of Louisville?",
  },
  {
    id: "task_carlos",
    driverId: "drv_carlos_mendez",
    type: "pta_risk",
    state: "suggested",
    urgency: "high",
    summary: "Traffic slowdown on I-40 — PTA slipping by 1h 15m for OKC drop.",
    recommendation: "Update PTA to 17:15 & flag pre-plan disruption.",
    cannedMessage: "Hi Carlos, traffic looks heavy ahead — are you able to keep your delivery window?",
    assignedToMe: true,
    assignedBy: "Dana Whitfield",
  },
  {
    id: "task_derek",
    driverId: "drv_derek_olsen",
    type: "late_appt",
    state: "suggested",
    urgency: "urgent",
    summary: "Strict 15:30 appointment at FedEx Ground — projected 15:55, running late.",
    recommendation: "Confirm driver can still make the hard appointment or notify the receiver.",
    cannedMessage:
      "Hi Derek, your 15:30 appointment at FedEx Ground is a hard time — are you still on track to make it?",
    assignedToMe: true,
    assignedBy: "Greg Coleman",
  },
  {
    id: "task_olivia",
    driverId: "drv_olivia_grant",
    type: "split_sleeper",
    state: "suggested",
    urgency: "med",
    summary: "Approaching off-duty — split sleeper not yet enabled.",
    recommendation: "Remind to enable split sleeper before going off-duty.",
    cannedMessage: "Hi Olivia, don't forget to enable split sleeper before your break to protect your drive time.",
  },
  {
    id: "task_deshawn",
    driverId: "drv_deshawn_riley",
    type: "breakdown",
    state: "requires_attention",
    summary: "Breakdown (DDI) flagged near I-55 MM 118 — ETA unknown.",
  },
  {
    id: "task_maria",
    driverId: "drv_maria_flores",
    type: "home_time",
    state: "requires_attention",
    summary: "PTA at risk with home time in 1 day — may miss requested days out.",
  },
  {
    id: "task_priya",
    driverId: "drv_priya_nair",
    type: "split_sleeper",
    state: "storyboard_resolved",
    summary: "Low on hours — Storyboard sent reroute notice and resolved.",
    sentMessage: "Heads up Priya, plan a 10-hr reset near Effingham, IL to stay legal for your window.",
  },
  {
    id: "task_brian",
    driverId: "drv_brian_thompson",
    type: "late_appt",
    state: "auto_resolved",
    summary: "Running late — driver already moving, back on track.",
    resolvedNote: "Driver resolved on their own.",
  },
  {
    id: "task_bianca_ecf",
    driverId: "drv_bianca_ortiz",
    type: "empty_call_failed",
    state: "requires_attention",
    urgency: "high",
    summary: "Preplan failed to dispatch — invalid trailer number entered on the unloaded call.",
    recommendation:
      "Open Active Context to correct the trailer number and dispatch the preplan — no driver message needed.",
    emptyCallFailure: { kind: "trailer", reason: "Invalid Trailer Number entered" },
  },
  {
    id: "task_janet_ecf",
    driverId: "drv_janet_wu",
    type: "empty_call_failed",
    state: "requires_attention",
    urgency: "high",
    summary: "Preplan failed to dispatch — deadhead exceeds the mileage limit.",
    recommendation:
      "Open Active Context to approve the deadhead miles and dispatch the preplan — no driver message needed.",
    emptyCallFailure: { kind: "mileage", reason: "Deadhead miles limit. Required: 280, Limit: 225" },
  },
];
